import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchCustomers } from '../actions/customers';
import { fetchMachines } from '../actions/machines';
import { fetchContracts } from '../actions/contracts';
import { fetchQRs } from '../actions/qrs';
import { fetchCalendars } from '../actions/calendars';
import LoadingProgress from '../components/LoadingProgress';
import ErrorMessage from '../components/ErrorMessage';

/* ─────────────────────────────────────────────────────────────────────────────
   RENTAL SANITY CHECK
   Checks all conditions required for online pre-rental (QR web booking) to
   work correctly per customer and per machine.
───────────────────────────────────────────────────────────────────────────── */

const ONLINE_WORKING_MODES = ['0', '4', '5', '6'];

const WORKING_MODE_NAMES = {
  '0': 'MTO DIARIO',
  '1': 'MTO DIARIO + SERVICIOS',
  '2': 'MTO DIARIO TIEMPO SIN PIN',
  '3': 'MTO DIARIO TIEMPO CON PIN',
  '4': 'OTO DIARIO SIN PIN',
  '5': 'OTO DIARIO CON PIN',
  '6': 'RESERVA ANTICIPADA',
  '7': 'TAQUILLAS AQUARIAZ',
  '8': 'PAQUETERÍA',
  '9': 'WRISTBAND',
  '11': 'APERTURA CON TELÉFONO',
};

const QR_MODE_NAMES = {
  0: 'MTO DIARIO',
  1: 'OTO DIARIO SIN PIN',
  2: 'BAQUEIRA',
  3: 'OTO DIARIO CON PIN',
  4: 'RESERVA ANTICIPADA',
  5: 'TIEMPO SIN PIN',
};

/* ── CSS injection ── */
const CSS = `
.rsc-page { width: 100%; font-family: inherit; color: #1e293b; box-sizing: border-box; }

/* Hero */
.rsc-hero { border-radius: 14px; padding: 36px 32px; margin-bottom: 24px; color: #fff; position: relative; overflow: hidden;
  background: linear-gradient(135deg, #0f172a 0%, #1e3a5f 55%, #1d4ed8 100%); }
.rsc-hero-badge { display: inline-flex; align-items: center; gap: 6px; background: rgba(255,255,255,0.15); border: 1px solid rgba(255,255,255,0.25);
  border-radius: 20px; padding: 4px 14px; font-size: 11px; font-weight: 700; letter-spacing: 1.1px; text-transform: uppercase; margin-bottom: 12px; }
.rsc-hero h1 { font-size: 28px; font-weight: 800; margin: 0 0 8px; line-height: 1.25; }
.rsc-hero p { font-size: 14px; opacity: 0.85; max-width: 700px; line-height: 1.65; margin: 0 0 20px; }
.rsc-hero-meta { display: flex; flex-wrap: wrap; gap: 18px; }
.rsc-hero-stat { display: flex; align-items: center; gap: 8px; background: rgba(255,255,255,0.1); border-radius: 10px; padding: 10px 16px; min-width: 120px; }
.rsc-hero-stat-num { font-size: 28px; font-weight: 900; }
.rsc-hero-stat-label { font-size: 12px; opacity: 0.85; line-height: 1.3; }
.rsc-hero-stat.err { background: rgba(220,38,38,0.25); border: 1px solid rgba(220,38,38,0.4); }
.rsc-hero-stat.warn { background: rgba(245,158,11,0.25); border: 1px solid rgba(245,158,11,0.4); }
.rsc-hero-stat.ok { background: rgba(22,163,74,0.25); border: 1px solid rgba(22,163,74,0.4); }
.rsc-hero-stat.total { background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.2); }

/* Controls bar */
.rsc-controls { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 20px; }
.rsc-search { flex: 1; min-width: 200px; max-width: 360px; position: relative; }
.rsc-search input { width: 100%; padding: 10px 14px 10px 38px; border: 1.5px solid #e2e8f0; border-radius: 8px;
  font-size: 13.5px; background: #fff; outline: none; transition: border-color 0.2s; }
.rsc-search input:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.1); }
.rsc-search-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #94a3b8; font-size: 14px; }
.rsc-filter-btn { padding: 10px 18px; border-radius: 8px; font-size: 13px; font-weight: 600; border: 1.5px solid #e2e8f0;
  cursor: pointer; transition: all 0.15s; background: #fff; color: #475569; }
.rsc-filter-btn:hover { border-color: #94a3b8; background: #f8fafc; }
.rsc-filter-btn.active { background: #1d4ed8; color: #fff; border-color: #1d4ed8; }
.rsc-refresh-btn { padding: 10px 18px; border-radius: 8px; font-size: 13px; font-weight: 600; border: 1.5px solid #e2e8f0;
  cursor: pointer; transition: all 0.15s; background: #fff; color: #475569; display: flex; align-items: center; gap: 6px; }
.rsc-refresh-btn:hover { border-color: #94a3b8; background: #f8fafc; }

/* Customer card */
.rsc-customer-card { background: #fff; border: 1.5px solid #e2e8f0; border-radius: 12px; margin-bottom: 16px; overflow: hidden; transition: box-shadow 0.15s; }
.rsc-customer-card:hover { box-shadow: 0 2px 12px rgba(0,0,0,0.07); }
.rsc-customer-card.has-errors { border-left: 4px solid #dc2626; }
.rsc-customer-card.has-warnings { border-left: 4px solid #f59e0b; }
.rsc-customer-card.all-ok { border-left: 4px solid #16a34a; }

/* Customer header */
.rsc-customer-header { display: flex; align-items: center; gap: 14px; padding: 18px 20px; cursor: pointer; user-select: none; }
.rsc-customer-header:hover { background: #f8fafc; }
.rsc-customer-avatar { width: 44px; height: 44px; border-radius: 10px; display: flex; align-items: center; justify-content: center;
  font-size: 18px; font-weight: 800; flex-shrink: 0; }
.rsc-customer-info { flex: 1; min-width: 0; }
.rsc-customer-name { font-size: 16px; font-weight: 700; color: #0f172a; margin-bottom: 2px; }
.rsc-customer-sub { font-size: 12.5px; color: #64748b; display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.rsc-customer-badges { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.rsc-count-badge { display: inline-flex; align-items: center; gap: 4px; padding: 4px 10px; border-radius: 20px; font-size: 12px; font-weight: 700; }
.rsc-count-badge.err { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }
.rsc-count-badge.warn { background: #fffbeb; color: #d97706; border: 1px solid #fde68a; }
.rsc-count-badge.ok { background: #f0fdf4; color: #16a34a; border: 1px solid #bbf7d0; }
.rsc-chevron { color: #94a3b8; font-size: 14px; transition: transform 0.2s; flex-shrink: 0; }
.rsc-chevron.open { transform: rotate(180deg); }

/* Customer body */
.rsc-customer-body { border-top: 1px solid #f1f5f9; padding: 16px 20px; }

/* Customer-level checks */
.rsc-section-title { font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: 1px; color: #94a3b8; margin-bottom: 10px; margin-top: 6px; }
.rsc-checks-grid { display: flex; flex-direction: column; gap: 6px; margin-bottom: 16px; }
.rsc-check-item { display: flex; align-items: flex-start; gap: 10px; padding: 10px 14px; border-radius: 8px; font-size: 13px; line-height: 1.5; }
.rsc-check-item.err { background: #fef2f2; color: #991b1b; border: 1px solid #fecaca; }
.rsc-check-item.warn { background: #fffbeb; color: #92400e; border: 1px solid #fde68a; }
.rsc-check-item.ok { background: #f0fdf4; color: #166534; border: 1px solid #bbf7d0; }
.rsc-check-item.info { background: #eff6ff; color: #1e40af; border: 1px solid #bfdbfe; }
.rsc-check-icon { font-size: 15px; flex-shrink: 0; margin-top: 1px; }
.rsc-check-msg { flex: 1; }
.rsc-check-link { margin-left: auto; font-size: 12px; font-weight: 600; color: #3b82f6; text-decoration: none; flex-shrink: 0; white-space: nowrap; padding-left: 8px; }
.rsc-check-link:hover { text-decoration: underline; }

/* Machine list inside customer */
.rsc-machines-title { font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: 1px; color: #94a3b8; margin-bottom: 10px; }
.rsc-machine-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; margin-bottom: 10px; overflow: hidden; }
.rsc-machine-card.has-errors { border-left: 3px solid #dc2626; }
.rsc-machine-card.has-warnings { border-left: 3px solid #f59e0b; }
.rsc-machine-card.all-ok { border-left: 3px solid #16a34a; }
.rsc-machine-header { display: flex; align-items: center; gap: 12px; padding: 12px 14px; cursor: pointer; }
.rsc-machine-header:hover { background: #f1f5f9; }
.rsc-machine-serial { font-size: 14px; font-weight: 700; color: #0f172a; font-family: monospace; }
.rsc-machine-meta { font-size: 12px; color: #64748b; display: flex; align-items: center; gap: 10px; flex: 1; flex-wrap: wrap; }
.rsc-machine-badges { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
.rsc-machine-body { border-top: 1px solid #e8ecf0; padding: 10px 14px; }

/* Empty state */
.rsc-empty { text-align: center; padding: 60px 20px; color: #94a3b8; }
.rsc-empty-icon { font-size: 48px; margin-bottom: 14px; }
.rsc-empty-title { font-size: 18px; font-weight: 700; color: #475569; margin-bottom: 6px; }
.rsc-empty-sub { font-size: 14px; }

/* Legends */
.rsc-legend { display: flex; align-items: center; gap: 16px; margin-bottom: 20px; padding: 12px 16px; background: #f8fafc; border-radius: 8px; border: 1px solid #e2e8f0; flex-wrap: wrap; }
.rsc-legend-item { display: flex; align-items: center; gap: 6px; font-size: 12.5px; color: #475569; }

/* QR tag */
.rsc-qr-tag { display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 600; }
.rsc-qr-tag.pay { background: #dbeafe; color: #1e40af; }
.rsc-qr-tag.free { background: #f0fdf4; color: #166534; }
.rsc-qr-tag.test { background: #fef9c3; color: #854d0e; }
.rsc-qr-tag.disabled { background: #f1f5f9; color: #94a3b8; text-decoration: line-through; }

@media (max-width: 768px) {
  .rsc-hero { padding: 24px 18px !important; }
  .rsc-hero h1 { font-size: 22px !important; }
  .rsc-hero-meta { gap: 10px; }
  .rsc-hero-stat { min-width: 100px; padding: 8px 12px; }
  .rsc-hero-stat-num { font-size: 22px; }
  .rsc-customer-header { flex-wrap: wrap; gap: 10px; }
  .rsc-customer-badges { width: 100%; }
}
`;

let stylesInjected = false;
function injectStyles() {
  if (stylesInjected) return;
  let el = document.getElementById('rental-sanity-styles');
  if (!el) {
    el = document.createElement('style');
    el.id = 'rental-sanity-styles';
    el.textContent = CSS;
    document.head.appendChild(el);
  }
  stylesInjected = true;
}

/* ── Helpers ── */

function toId(val) {
  if (!val) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'object' && val._id) return val._id.toString();
  return val.toString();
}

function formatSleepHHMM(hhmm) {
  if (!hhmm || hhmm.length < 4) return hhmm || '--';
  return `${hhmm.slice(0, 2)}:${hhmm.slice(2)}`;
}

/* ── Core check logic ── */

function runCustomerChecks(customer, allMachines, allContracts, allQRs) {
  const errors = [];
  const warnings = [];

  // --- Redsys (online payment gateway) ---
  const r = customer.Redsys;
  if (!r) {
    errors.push({ id: 'redsys', msg: 'TPV Virtual (Redsys) no configurado — sin pasarela de pago online no se pueden cobrar pre-reservas.' });
  } else {
    const missing = [];
    if (!r.MERCHANTCODE) missing.push('Código de comercio');
    if (!r.SECRETKEY)    missing.push('Clave de cifrado');
    if (!r.CURRENCY)     missing.push('Moneda');
    if (!r.TERMINAL)     missing.push('Terminal');
    if (!r.MERCHANTURL)  missing.push('URL Tienda');
    if (missing.length > 0) {
      errors.push({ id: 'redsys-incomplete', msg: `TPV Virtual (Redsys) incompleto — faltan: ${missing.join(', ')}.` });
    }
  }

  // --- Machines ---
  const customerMachines = allMachines.filter(
    m => toId(m.Customer) === customer._id.toString()
  );
  if (customerMachines.length === 0) {
    warnings.push({ id: 'no-machines', msg: 'Sin máquinas asignadas. Sin máquinas no hay pre-alquiler.' });
  }

  // --- QRs (customer level) ---
  const customerQRs = allQRs.filter(
    q => toId(q.Customer) === customer._id.toString()
  );
  if (customerQRs.length === 0) {
    errors.push({ id: 'no-qrs', msg: 'Sin QRs configurados — los clientes finales no podrán acceder al pre-alquiler online.' });
  } else if (!customerQRs.some(q => q.Enabled)) {
    errors.push({ id: 'all-qrs-disabled', msg: 'Tiene QRs configurados pero ninguno está habilitado.' });
  }

  // --- Per-machine sanity ---
  const machineResults = customerMachines.map(machine =>
    runMachineChecks(machine, allContracts, customerQRs)
  );

  // Aggregate machine-level counts for the summary
  const totalMachineErrors = machineResults.reduce((n, r) => n + r.errors.length, 0);
  const totalMachineWarnings = machineResults.reduce((n, r) => n + r.warnings.length, 0);

  return { errors, warnings, machineResults, customerQRs, totalMachineErrors, totalMachineWarnings };
}

function runMachineChecks(machine, allContracts, customerQRs) {
  const errors = [];
  const warnings = [];

  // Locate the full contract for this machine
  const contract = allContracts.find(
    c => toId(c.Machine) === machine._id.toString()
  );

  if (!contract) {
    errors.push({ id: 'no-contract', msg: 'Sin contrato asignado — la máquina no puede operar.' });
    return { machine, contract: null, errors, warnings, zoneQRs: [] };
  }

  // Contract validity
  if (contract.Status !== 'VALID') {
    errors.push({ id: 'contract-invalid', msg: `Contrato en estado "${contract.Status}" — debe estar en VALID.` });
  }

  // Blocked
  if (contract.Blocked) {
    errors.push({ id: 'contract-blocked', msg: 'Contrato bloqueado manualmente — ventas online deshabilitadas.' });
  }

  // Working mode compatibility
  const workingModeName = WORKING_MODE_NAMES[contract.WorkingMode] ?? contract.WorkingMode;
  if (!ONLINE_WORKING_MODES.includes(contract.WorkingMode)) {
    errors.push({
      id: 'working-mode',
      msg: `Modo de trabajo "${workingModeName}" (${contract.WorkingMode}) no compatible con pre-alquiler online. `
        + `Requiere: MTO DIARIO (0), OTO DIARIO SIN PIN (4), OTO DIARIO CON PIN (5) o RESERVA ANTICIPADA (6).`,
    });
  }

  // Hybrid mode
  if (!contract.HybridMode) {
    errors.push({
      id: 'hybrid-mode',
      msg: 'Modo Híbrido desactivado — sin modo híbrido no pueden coexistir ventas físicas y reservas online simultáneamente.',
    });
  }

  // Sleep start: must be >= 02:00 so the machine doesn't go offline too early
  const sleepStart = machine.SleepStart || '2300';
  // Flag if machine sleeps before 02:00: covers both evening (>= 18:00) and early morning (< 02:00)
  const sleepIsTooEarly = sleepStart < '0200' || sleepStart >= '1800';
  if (sleepIsTooEarly) {
    errors.push({
      id: 'sleep-start',
      msg: `Sleep Start ${formatSleepHHMM(sleepStart)} — la máquina entra en modo noche antes de las 02:00. Las reservas tardías fallarán. Debe ser ≥ 02:00.`,
    });
  }

  // Local calendar with no calendar assigned
  if (contract.LocalCalendar) {
    if (!contract.Calendar) {
      errors.push({
        id: 'local-calendar',
        msg: 'Calendario local activado pero sin calendario asignado — los precios dinámicos no se aplicarán y la venta puede fallar.',
      });
    } else {
      // Calendar assigned, check it has at least some entries (informational — we can only check name)
      const calendarName = contract.Calendar.Name || contract.Calendar;
      warnings.push({
        id: 'local-calendar-ok',
        msg: `Calendario local activo: "${calendarName}". Asegúrese de que tenga entradas para todas las fechas de venta.`,
        level: 'info',
      });
    }
  }

  // QR linkage for this zone
  // Zone 0 acts as a universal/global QR that covers all zones
  const contractZone = contract.Zone ?? 0;
  const directZoneQRs = customerQRs.filter(q => q.Zone === contractZone);
  const globalQRs = contractZone !== 0 ? customerQRs.filter(q => q.Zone === 0) : [];
  // Effective set: direct zone QRs if any, otherwise fall back to global zone-0 QRs
  const zoneQRs = directZoneQRs.length > 0 ? directZoneQRs : globalQRs;
  const usingGlobalFallback = directZoneQRs.length === 0 && globalQRs.length > 0;

  // Determine required QR mode — all pre-rental QRs must be mode 4 (RESERVA ANTICIPADA)
  const expectedQRMode = 4;

  let hasValidQR = false;

  if (directZoneQRs.length === 0 && globalQRs.length === 0) {
    errors.push({
      id: 'qr-zone',
      msg: `Sin QR configurado para zona ${contractZone} de modo "RESERVA ANTICIPADA" (ni QR global zona 0) — los clientes finales no tendrán URL de acceso.`,
    });
  } else {
    const enabledZoneQRs = zoneQRs.filter(q => q.Enabled);
    // Valid QRs must be enabled AND be mode 4 (RESERVA ANTICIPADA)
    const validQRs = enabledZoneQRs.filter(q => q.Mode === expectedQRMode);

    if (enabledZoneQRs.length === 0) {
      errors.push({
        id: 'qr-zone-disabled',
        msg: usingGlobalFallback
          ? `Sin QR específico para zona ${contractZone}. El QR global (zona 0) existe pero está deshabilitado — habilitar para permitir pre-alquiler.`
          : `QR de zona ${contractZone} existe pero está deshabilitado — habilitar para permitir pre-alquiler.`,
      });
    } else if (validQRs.length === 0) {
      // Enabled QRs exist but none are mode 4 (RESERVA ANTICIPADA) → blocking error
      errors.push({
        id: 'qr-mode-mismatch',
        msg: `QR de zona ${contractZone} activo pero con modo incorrecto (${enabledZoneQRs.map(q => `Modo ${q.Mode}`).join(', ')}) — se requiere modo "RESERVA ANTICIPADA" (4).`,
      });
    } else {
      hasValidQR = true;
      if (usingGlobalFallback) {
        warnings.push({
          id: 'qr-zone-global',
          msg: `Sin QR específico para zona ${contractZone} — se usará el QR global (zona 0). Funcional pero sin segmentación por zona.`,
        });
      }
      const payQRs = validQRs.filter(q => q.Type === 'pay');
      if (payQRs.length === 0) {
        warnings.push({
          id: 'qr-type',
          msg: `QR de zona ${contractZone} activo pero de tipo "${validQRs[0]?.Type}" — sólo los QR de tipo "pay" generan ingresos reales.`,
        });
      }
    }
  }

  return { machine, contract, errors, warnings, zoneQRs, hasValidQR };
}

/* ── Sub-components ── */

function StatusIcon({ level }) {
  if (level === 'ok')   return <span style={{ color: '#16a34a', fontSize: 15 }}>✓</span>;
  if (level === 'warn') return <span style={{ color: '#d97706', fontSize: 15 }}>⚠</span>;
  if (level === 'info') return <span style={{ color: '#2563eb', fontSize: 15 }}>ℹ</span>;
  return <span style={{ color: '#dc2626', fontSize: 15 }}>✗</span>;
}

function CheckItem({ item, level }) {
  const lvl = item.level || level || 'err';
  return (
    <div className={`rsc-check-item ${lvl}`}>
      <span className="rsc-check-icon"><StatusIcon level={lvl} /></span>
      <span className="rsc-check-msg">{item.msg}</span>
    </div>
  );
}

function QRTag({ qr }) {
  const cls = !qr.Enabled ? 'disabled' : (qr.Type || 'pay');
  return (
    <span className={`rsc-qr-tag ${cls}`}>
      {qr.Code} {qr.Type && `[${qr.Type}]`} {!qr.Enabled && '(OFF)'}
    </span>
  );
}

class MachineResult extends Component {
  constructor(props) {
    super(props);
    this.state = { expanded: props.expanded || false };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.expandAllGen !== this.props.expandAllGen) {
      this.setState({ expanded: this.props.expandAllValue });
    }
  }

  toggle = () => this.setState(s => ({ expanded: !s.expanded }));

  render() {
    const { result } = this.props;
    const { machine, contract, errors, warnings, zoneQRs, hasValidQR } = result;
    const { expanded } = this.state;

    const hasErrors = errors.length > 0;
    const hasWarnings = warnings.length > 0;
    const cardClass = hasErrors ? 'has-errors' : hasWarnings ? 'has-warnings' : 'all-ok';
    const contractZone = contract?.Zone ?? '—';

    return (
      <div className={`rsc-machine-card ${cardClass}`}>
        <div className="rsc-machine-header" onClick={this.toggle}>
          <span className="rsc-machine-serial">{machine.Serial}</span>
          <span className="rsc-machine-meta">
            {contract
              ? <>
                  <span>Contrato #{contract.number}</span>
                  <span>·</span>
                  <span>Zona {contractZone}</span>
                  <span>·</span>
                  <span>
                    {WORKING_MODE_NAMES[contract.WorkingMode] ?? contract.WorkingMode}
                    {contract.HybridMode && <span style={{ marginLeft: 4, color: '#16a34a', fontSize: 11, fontWeight: 700 }}>[HYBRID]</span>}
                  </span>
                  <span>·</span>
                  <span style={{ color: contract.LocalCalendar ? (contract.Calendar ? '#7c3aed' : '#dc2626') : '#94a3b8', fontWeight: 600 }}>
                    📅 Calendario Local: {contract.LocalCalendar ? (contract.Calendar ? 'ON' : 'ON ⚠') : 'OFF'}
                  </span>
                </>
              : <span style={{ color: '#dc2626' }}>Sin contrato</span>
            }
            <span>·</span>
            <span style={{ color: hasValidQR ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
              QR zona {contract?.Zone ?? '—'}: {hasValidQR ? 'ON' : 'OFF'}
            </span>
            <span>·</span>
            <span style={{ color: (() => { const s = machine.SleepStart || '2300'; return (s < '0200' || s >= '1800') ? '#d97706' : '#64748b'; })(), fontWeight: 600 }}>
              Sleep {formatSleepHHMM(machine.SleepStart || '2300')}
            </span>
          </span>
          <div className="rsc-machine-badges">
            {hasErrors > 0 && (
              <span className="rsc-count-badge err">✗ {errors.length}</span>
            )}
            {hasWarnings > 0 && (
              <span className="rsc-count-badge warn">⚠ {warnings.length}</span>
            )}
            {!hasErrors && !hasWarnings && (
              <span className="rsc-count-badge ok">✓ OK</span>
            )}
          </div>
          <span className={`rsc-chevron ${expanded ? 'open' : ''}`}>▼</span>
        </div>

        {expanded && (
          <div className="rsc-machine-body">
            {/* Machine links */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
              <Link to={`/machines/${machine._id}`} style={{ fontSize: 12, color: '#3b82f6', fontWeight: 600 }}>
                → Ver máquina
              </Link>
              {contract && (
                <Link to={`/contracts/${contract._id}`} style={{ fontSize: 12, color: '#3b82f6', fontWeight: 600 }}>
                  → Ver contrato #{contract.number}
                </Link>
              )}
              {contract?.Calendar && (
                <Link to={`/calendars/${typeof contract.Calendar === 'object' ? contract.Calendar._id : contract.Calendar}`}
                  style={{ fontSize: 12, color: '#7c3aed', fontWeight: 600 }}>
                  → Ver calendario
                </Link>
              )}
            </div>

            {/* All errors */}
            {errors.length > 0 && (
              <>
                <div className="rsc-section-title">Errores críticos ({errors.length})</div>
                <div className="rsc-checks-grid">
                  {errors.map(e => <CheckItem key={e.id} item={e} level="err" />)}
                </div>
              </>
            )}

            {/* All warnings */}
            {warnings.length > 0 && (
              <>
                <div className="rsc-section-title">Avisos ({warnings.length})</div>
                <div className="rsc-checks-grid">
                  {warnings.map(w => <CheckItem key={w.id} item={w} level={w.level || 'warn'} />)}
                </div>
              </>
            )}

            {/* All OK */}
            {errors.length === 0 && warnings.length === 0 && (
              <div className="rsc-check-item ok">
                <span className="rsc-check-icon"><StatusIcon level="ok" /></span>
                <span className="rsc-check-msg">
                  Todas las verificaciones pasaron. Esta máquina está lista para pre-alquiler online.
                </span>
              </div>
            )}

            {/* QRs for this zone */}
            {zoneQRs.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div className="rsc-section-title">QRs de zona {contractZone}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {zoneQRs.map(q => (
                    <Link key={q._id} to={`/qrs/${q._id}`} style={{ textDecoration: 'none' }}>
                      <QRTag qr={q} />
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
}

class CustomerResult extends Component {
  constructor(props) {
    super(props);
    this.state = { expanded: props.defaultExpanded || false };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.expandAllGen !== this.props.expandAllGen) {
      this.setState({ expanded: this.props.expandAllValue });
    }
  }

  toggle = () => this.setState(s => ({ expanded: !s.expanded }));

  render() {
    const { result } = this.props;
    const { customer, errors, warnings, machineResults, customerQRs, totalMachineErrors, totalMachineWarnings } = result;
    const { expanded } = this.state;

    const totalErrors = errors.length + totalMachineErrors;
    const totalWarnings = warnings.length + totalMachineWarnings;

    const hasErrors = totalErrors > 0;
    const hasWarnings = totalWarnings > 0;
    const cardClass = hasErrors ? 'has-errors' : hasWarnings ? 'has-warnings' : 'all-ok';

    // Avatar color
    const avatarColor = hasErrors ? '#dc2626' : hasWarnings ? '#f59e0b' : '#16a34a';
    const avatarBg = hasErrors ? '#fef2f2' : hasWarnings ? '#fffbeb' : '#f0fdf4';
    const initial = (customer.Fullname || customer.Email || '?').charAt(0).toUpperCase();

    return (
      <div className={`rsc-customer-card ${cardClass}`}>
        <div className="rsc-customer-header" onClick={this.toggle}>
          <div className="rsc-customer-avatar" style={{ background: avatarBg, color: avatarColor }}>
            {initial}
          </div>
          <div className="rsc-customer-info">
            <div className="rsc-customer-name">{customer.Fullname || customer.Email}</div>
            <div className="rsc-customer-sub">
              <span>{machineResults.length} máquina{machineResults.length !== 1 ? 's' : ''}</span>
              <span>·</span>
              <span>{customerQRs.length} QR{customerQRs.length !== 1 ? 's' : ''}</span>
              <span>·</span>
              <span style={{ color: customer.Redsys?.MERCHANTCODE ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
                Redsys {customer.Redsys?.MERCHANTCODE ? '✓' : '✗'}
              </span>
              {customer.Email && <span>· {customer.Email}</span>}
            </div>
          </div>
          <div className="rsc-customer-badges">
            {hasErrors && <span className="rsc-count-badge err">✗ {totalErrors} error{totalErrors !== 1 ? 'es' : ''}</span>}
            {!hasErrors && hasWarnings && <span className="rsc-count-badge warn">⚠ {totalWarnings} aviso{totalWarnings !== 1 ? 's' : ''}</span>}
            {!hasErrors && !hasWarnings && <span className="rsc-count-badge ok">✓ Listo</span>}
          </div>
          <span className={`rsc-chevron ${expanded ? 'open' : ''}`}>▼</span>
        </div>

        {expanded && (
          <div className="rsc-customer-body">
            {/* Customer links */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
              <Link to={`/customers/${customer._id}`} style={{ fontSize: 12, color: '#3b82f6', fontWeight: 600 }}>
                → Ver cliente
              </Link>
              <Link to={`/qrs`} style={{ fontSize: 12, color: '#3b82f6', fontWeight: 600 }}>
                → Ver QRs
              </Link>
              <Link to={`/contracts`} style={{ fontSize: 12, color: '#3b82f6', fontWeight: 600 }}>
                → Ver contratos
              </Link>
            </div>

            {/* Customer-level errors */}
            {errors.length > 0 && (
              <>
                <div className="rsc-section-title">Configuración de cliente — Errores ({errors.length})</div>
                <div className="rsc-checks-grid">
                  {errors.map(e => <CheckItem key={e.id} item={e} level="err" />)}
                </div>
              </>
            )}

            {/* Customer-level warnings */}
            {warnings.length > 0 && (
              <>
                <div className="rsc-section-title">Configuración de cliente — Avisos ({warnings.length})</div>
                <div className="rsc-checks-grid">
                  {warnings.map(w => <CheckItem key={w.id} item={w} level="warn" />)}
                </div>
              </>
            )}

            {/* Customer-level all OK */}
            {errors.length === 0 && warnings.length === 0 && (
              <div className="rsc-checks-grid" style={{ marginBottom: 14 }}>
                <div className="rsc-check-item ok">
                  <span className="rsc-check-icon"><StatusIcon level="ok" /></span>
                  <span className="rsc-check-msg">
                    Configuración de cliente correcta (Redsys, QRs).
                  </span>
                </div>
              </div>
            )}

            {/* Machines */}
            {machineResults.length > 0 ? (
              <>
                <div className="rsc-machines-title">Máquinas ({machineResults.length})</div>
                {machineResults.map(r => (
                  <MachineResult
                    key={r.machine._id}
                    result={r}
                    expanded={r.errors.length > 0}
                    expandAllGen={this.props.expandAllGen}
                    expandAllValue={this.props.expandAllValue}
                  />
                ))}
              </>
            ) : (
              <div className="rsc-check-item warn">
                <span className="rsc-check-icon">⚠</span>
                <span className="rsc-check-msg">Sin máquinas asignadas a este cliente.</span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
}

/* ─────────────────────────────────────────────────────────────────────────────
   Main view
───────────────────────────────────────────────────────────────────────────── */

class RentalSanityView extends Component {
  constructor(props) {
    super(props);
    this.state = {
      searchTerm: '',
      showOnlyIssues: false,
      refreshKey: 0,
      expandAllGen: 0,
      expandAllValue: false,
    };
  }

  componentDidMount() {
    injectStyles();
    this.fetchAll();
  }

  fetchAll = () => {
    const { fetchCustomers, fetchMachines, fetchContracts, fetchQRs, fetchCalendars } = this.props;
    fetchCustomers();
    fetchMachines();
    fetchContracts();
    fetchQRs();
    fetchCalendars();
  };

  handleExpandAll  = () => this.setState(s => ({ expandAllGen: s.expandAllGen + 1, expandAllValue: true }));
  handleCollapseAll = () => this.setState(s => ({ expandAllGen: s.expandAllGen + 1, expandAllValue: false }));

  handleRefresh = () => {
    this.setState(s => ({ refreshKey: s.refreshKey + 1 }));
    this.fetchAll();
  };

  render() {
    const { customers, machines, contracts, qrs, role, type } = this.props;
    const { searchTerm, showOnlyIssues, expandAllGen, expandAllValue } = this.state;

    if (role === 'CUSTOMER') {
      return <div><h1><span className="text-vw-light">ACCESO NO AUTORIZADO</span></h1></div>;
    }

    const allLoaded = customers && machines && contracts && qrs;

    if (!allLoaded) {
      return <LoadingProgress />;
    }

    // Filter: only licencia customers (not PDA/CASH/MANAGER sub-accounts)
    const relevantTypes = new Set(['LICENCIA_BASICA', 'LICENCIA_PRO', 'GROUP']);
    let filteredCustomers = customers.filter(c => relevantTypes.has(c.Type));

    // Filter: only customers with at least one machine whose contract has HybridMode active
    filteredCustomers = filteredCustomers.filter(customer => {
      const customerMachines = machines.filter(m => toId(m.Customer) === customer._id.toString());
      return customerMachines.some(machine => {
        const contractId = toId(machine.Contract);
        const contract = contracts.find(c => c._id.toString() === contractId);
        return contract && contract.HybridMode === true;
      });
    });

    // Search filter
    if (searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();
      filteredCustomers = filteredCustomers.filter(c =>
        (c.Fullname || '').toLowerCase().includes(term) ||
        (c.Email || '').toLowerCase().includes(term)
      );
    }

    // Run all checks
    const results = filteredCustomers.map(customer => ({
      customer,
      ...runCustomerChecks(customer, machines, contracts, qrs),
    }));

    // "Only issues" filter
    const displayResults = showOnlyIssues
      ? results.filter(r =>
          r.errors.length > 0 ||
          r.totalMachineErrors > 0 ||
          r.warnings.length > 0 ||
          r.totalMachineWarnings > 0
        )
      : results;

    // Summary stats
    const totalCustomers = results.length;
    const errorCustomers = results.filter(r => r.errors.length > 0 || r.totalMachineErrors > 0).length;
    const warnCustomers = results.filter(r =>
      r.errors.length === 0 && r.totalMachineErrors === 0 &&
      (r.warnings.length > 0 || r.totalMachineWarnings > 0)
    ).length;
    const okCustomers = totalCustomers - errorCustomers - warnCustomers;

    // Auto-expand error customers when filtering by issues
    const defaultExpand = showOnlyIssues;

    return (
      <div className="rsc-page">

        {/* ── Hero ── */}
        <div className="rsc-hero">
          <div className="rsc-hero-badge">
            <span>🔍</span> Diagnóstico de Pre-alquiler Online
          </div>
          <h1>Rental Sanity Check</h1>
          <p>
            Verifica que todos los clientes, máquinas, contratos, QRs y calendarios están correctamente
            configurados para que el pre-alquiler online (reserva web) funcione correctamente.
            Se revisan: pasarela de pago, modo de trabajo, modo híbrido, activación QR, horario de
            descanso, vinculación de calendarios y alineación de zonas.
          </p>
          <div className="rsc-hero-meta">
            <div className="rsc-hero-stat total">
              <span className="rsc-hero-stat-num">{totalCustomers}</span>
              <span className="rsc-hero-stat-label">Clientes<br/>analizados</span>
            </div>
            <div className={`rsc-hero-stat ${errorCustomers > 0 ? 'err' : 'ok'}`}>
              <span className="rsc-hero-stat-num">{errorCustomers}</span>
              <span className="rsc-hero-stat-label">Con<br/>errores</span>
            </div>
            <div className={`rsc-hero-stat ${warnCustomers > 0 ? 'warn' : 'ok'}`}>
              <span className="rsc-hero-stat-num">{warnCustomers}</span>
              <span className="rsc-hero-stat-label">Con<br/>avisos</span>
            </div>
            <div className="rsc-hero-stat ok">
              <span className="rsc-hero-stat-num">{okCustomers}</span>
              <span className="rsc-hero-stat-label">Totalmente<br/>correctos</span>
            </div>
          </div>
        </div>

        {/* ── Legend ── */}
        <div className="rsc-legend">
          <span style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginRight: 4 }}>Leyenda:</span>
          <span className="rsc-legend-item"><StatusIcon level="err" /> Error crítico — impide el pre-alquiler</span>
          <span className="rsc-legend-item"><StatusIcon level="warn" /> Aviso — puede afectar la venta</span>
          <span className="rsc-legend-item"><StatusIcon level="info" /> Informativo — revisar</span>
          <span className="rsc-legend-item"><StatusIcon level="ok" /> Correcto</span>
        </div>

        {/* ── Controls ── */}
        <div className="rsc-controls">
          <div className="rsc-search">
            <span className="rsc-search-icon">🔍</span>
            <input
              type="text"
              placeholder="Buscar cliente por nombre o email…"
              value={searchTerm}
              onChange={e => this.setState({ searchTerm: e.target.value })}
            />
          </div>
          <button
            className={`rsc-filter-btn ${showOnlyIssues ? 'active' : ''}`}
            onClick={() => this.setState(s => ({ showOnlyIssues: !s.showOnlyIssues }))}
          >
            {showOnlyIssues ? '✓ Solo con problemas' : 'Mostrar solo problemas'}
          </button>
          <button className="rsc-refresh-btn" onClick={this.handleRefresh}>
            ↻ Actualizar
          </button>
          <button className="rsc-refresh-btn" onClick={this.handleExpandAll} style={{ background: '#eff6ff', color: '#1e40af', borderColor: '#bfdbfe' }}>
            ⊞ Expandir todo
          </button>
          <button className="rsc-refresh-btn" onClick={this.handleCollapseAll} style={{ background: '#f8fafc', color: '#475569', borderColor: '#e2e8f0' }}>
            ⊟ Colapsar todo
          </button>
          <span style={{ marginLeft: 'auto', fontSize: 12.5, color: '#94a3b8' }}>
            Mostrando {displayResults.length} de {totalCustomers} clientes
          </span>
        </div>

        {/* ── Results ── */}
        {displayResults.length === 0 ? (
          <div className="rsc-empty">
            <div className="rsc-empty-icon">
              {showOnlyIssues ? '🎉' : '🔍'}
            </div>
            <div className="rsc-empty-title">
              {showOnlyIssues
                ? '¡Sin problemas detectados!'
                : 'No se encontraron clientes'
              }
            </div>
            <div className="rsc-empty-sub">
              {showOnlyIssues
                ? 'Todos los clientes con Modo Híbrido activo tienen la configuración de pre-alquiler online correcta.'
                : 'Prueba con otro término de búsqueda.'
              }
            </div>
          </div>
        ) : (
          displayResults.map(r => (
            <CustomerResult
              key={r.customer._id}
              result={r}
              defaultExpanded={defaultExpand || (r.errors.length > 0 || r.totalMachineErrors > 0)}
              expandAllGen={expandAllGen}
              expandAllValue={expandAllValue}
            />
          ))
        )}

        {/* ── Footer note ── */}
        <div style={{ marginTop: 24, padding: '12px 16px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12.5, color: '#64748b', lineHeight: 1.6 }}>
          <strong>Modos compatibles con pre-alquiler online:</strong> MTO DIARIO (0), OTO DIARIO SIN PIN (4), OTO DIARIO CON PIN (5), RESERVA ANTICIPADA (6).
          {' '}El <strong>Modo Híbrido</strong> debe estar activo para permitir ventas físicas y online simultáneamente.
          {' '}El <strong>Sleep Start</strong> debe ser ≥ 02:00 para no interrumpir reservas de madrugada.
          {' '}Si el <strong>Calendario Local</strong> está activo, debe haber un calendario asignado con precios.
          {' '}Cada zona debe tener al menos un <strong>QR habilitado de tipo "pay"</strong>.
        </div>

      </div>
    );
  }
}

const mapStateToProps = ({ auth, customers, machines, contracts, qrs, calendars }) => ({
  role: auth.role,
  type: auth.type,
  customers: customers.customers,
  machines: machines.machines,
  contracts: contracts.contracts,
  qrs: qrs.qrs,
  calendars: calendars.calendars,
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    { fetchCustomers, fetchMachines, fetchContracts, fetchQRs, fetchCalendars },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(RentalSanityView);
